import type { ApolloError } from 'apollo-client';

import { useExperimentalLazyQuery } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalLazyQuery';
import { MediaType } from '@confluence/graphql/entry-points/MediaType';

import type {
	ShareAndRestrictDialogInviteCapabilitiesQuery as ShareAndRestrictDialogInviteCapabilitiesQueryData,
	ShareAndRestrictDialogInviteCapabilitiesQueryVariables,
} from './__types__/ShareAndRestrictDialogInviteCapabilitiesQuery';

type DirectInviteMode = 'ANYONE' | 'DOMAIN_RESTRICTED' | 'NONE';
type InvitePendingApprovalMode = 'ANYONE' | 'NONE';

type InviteCapabilitiesItem = {
	resourceARI?: string;
	directInvite?: {
		mode?: DirectInviteMode;
		domains?: string[];
	};
	invitePendingApproval?: {
		mode?: InvitePendingApprovalMode;
	};
};

type InviteCapabilitiesResponse = InviteCapabilitiesItem[];

export const useExperimentalInvitePermissionsQuery = ({
	variables,
	onCompleted,
	onError,
}: {
	variables: ShareAndRestrictDialogInviteCapabilitiesQueryVariables;
	onCompleted?: (data: ShareAndRestrictDialogInviteCapabilitiesQueryData) => void;
	onError?: (error: ApolloError) => void;
}) => {
	return useExperimentalLazyQuery<
		ShareAndRestrictDialogInviteCapabilitiesQueryData,
		ShareAndRestrictDialogInviteCapabilitiesQueryVariables,
		InviteCapabilitiesResponse
	>({
		url: `/gateway/api/v3/invitations/capabilities?resource=${variables.resourceARI}`,
		method: 'GET',
		credentials: 'include',
		headers: {
			'Content-Type': MediaType.JSON,
		},
		parseResult: (response: InviteCapabilitiesResponse) => {
			for (const item of response) {
				if (item?.resourceARI?.includes('confluence')) {
					return {
						experimentalInvitePermissions: {
							directInviteMode: item?.directInvite?.mode ?? null,
							directInviteDomains: item?.directInvite?.domains ?? null,
							invitePendingApprovalMode: item?.invitePendingApproval?.mode ?? null,
						},
					};
				}
			}
			return {
				experimentalInvitePermissions: {
					directInviteMode: null,
					directInviteDomains: null,
					invitePendingApprovalMode: null,
				},
			};
		},
		variables,
		onCompleted,
		onError,
	});
};
